import { Maybe }		from "ts-base/maybe";
import { Arrays }		from "ts-base/arrays";
import { Semigroup }	from "ts-base/semigroup";

import { Choice }		from "@v3/geko/form/choice";

export type ChoiceList<T>	= {
	choices:	ReadonlyArray<Choice<T>>,
	// NOTE this is exclusively used to render feedback
	label:		(model:T) => string,
	key:		(model:T) => string,
	model:		(key:string) => T,
};

export namespace ChoiceList {
	export const of	= <T>(choices:ReadonlyArray<Choice<T>>):ChoiceList<T>	=> ({
		choices:	choices,
		label:		(model) => byModel(choices, model).label,
		key:		(model) => byModel(choices, model).key,
		model:		(key)	=> byKey(choices, key).model,
	});

	export const append	= <T>(self:ChoiceList<T>) => <S>(choice:Choice<S>):ChoiceList<S|T>	=>
		concat(self, single(choice));

	export const prepend	= <T>(self:ChoiceList<T>) => <S>(choice:Choice<S>):ChoiceList<S|T>	=>
		concat(single(choice), self);

	export const concat	= <S, T>(a:ChoiceList<S>, b:ChoiceList<T>):ChoiceList<S|T>	=>
		of<S|T>([ ...a.choices, ...b.choices ]);

	export const single	= <T>(choice:Choice<T>):ChoiceList<T>	=>
		of([ choice ]);

	const byKey	= <T>(choices:ReadonlyArray<Choice<T>>, key:string):Choice<T>	=>
		Maybe.unsafeGet(
			Arrays.find(choices)((it) => it.key === key)
		);

	const byModel	= <T>(choices:ReadonlyArray<Choice<T>>, model:T):Choice<T>	=>
		Maybe.unsafeGet(
			Arrays.find(choices)((it) => it.model === model)
		);

	export const semigroup	= <T>():Semigroup<ChoiceList<T>>	=>
		Semigroup.fromFunction(concat);
}
